import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Button,
  Tooltip,
} from '@chakra-ui/react';

import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';
import { useMemo } from 'react';
import {
  AddCircleOutline,
  ArchiveOutline,
  ArrowRightOutline,
  formatCurrency,
  MoreCircleOutline,
  PridePeopleCount,
} from '@aslanpay/portal-common-lib';
import getPointsValue from 'app/utils/points';

import { PotCardProps } from './PotCard.types';

function PotCard({
  data,
  onAddFundsClick,
  onArchiveClick,
  onManageClick,
  onPeopleCountClick,
  onReclaimFundsClick,
}: PotCardProps) {
  const totalMembers = data.memberCount + data.managerCount;

  const balance = data.pot?.balance || 0;
  const balanceValue = getPointsValue(balance);

  const renderActions = useMemo(() => {
    // if there are no actions dont render the menu
    if (!onAddFundsClick && !onReclaimFundsClick && !onArchiveClick) {
      return null;
    }

    return (
      <Menu autoSelect={false} strategy="fixed">
        <MenuButton
          as={IconButton}
          aria-label="Options"
          icon={<MoreCircleOutline boxSize="20px" />}
          variant="menu"
          onClick={(e) => e.stopPropagation()}
        />
        <Portal>
          <MenuList>
            {onAddFundsClick && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddFundsClick();
                }}
                icon={<AddCircleOutline boxSize="20px" />}
              >
                Add funds
              </MenuItem>
            )}

            <Tooltip
              hasArrow
              arrowPadding={0}
              placement="top"
              label="The pot must have a balance greater than 0 to reclaim funds"
              isDisabled={!!onReclaimFundsClick}
            >
              <Box display="block">
                <MenuItem
                  isDisabled={!onReclaimFundsClick}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReclaimFundsClick?.();
                  }}
                  icon={<ArrowRightOutline boxSize="20px" />}
                >
                  Reclaim funds
                </MenuItem>
              </Box>
            </Tooltip>

            {onArchiveClick && (
              <MenuItem
                color="brand.danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onArchiveClick();
                }}
                icon={<ArchiveOutline boxSize="20px" />}
              >
                Archive pot
              </MenuItem>
            )}
          </MenuList>
        </Portal>
      </Menu>
    );
  }, [onAddFundsClick, onArchiveClick, onReclaimFundsClick]);

  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor="brand.lightGray1"
      p="20px"
      gap="15px"
      flex="1"
      borderRadius="5px"
      h="full"
      _hover={{
        boxShadow: 'lg',
      }}
      cursor="pointer"
      onClick={onManageClick}
    >
      <Flex direction="column" gap="20px" flex="1" h="full">
        <Flex justify="space-between">
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <PridePeopleCount total={totalMembers} onClick={onPeopleCountClick} />
          </div>

          {renderActions}
        </Flex>

        <Flex direction="column" gap="8px" align="flex-start">
          <Text as="h4" textStyle="h4Bold" color="brand.purple">
            {getGroupDisplayNameWithParents(data)}
          </Text>
          <Text as="h5" textStyle="h5SemiBold" color="brand.mutedPurple">
            {balance.toLocaleString()} points
          </Text>
          <Text as="p" textStyle="smallRegular" color="brand.mutedPurple">
            Worth {formatCurrency(balanceValue)}
          </Text>
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center">
        <Text>{data.managerCount} pot manager(s)</Text>

        {onManageClick && (
          <Button
            size="lg"
            variant="ghost"
            onClick={onManageClick}
            rightIcon={<ArrowRightOutline />}
          >
            Manage
          </Button>
        )}
      </Flex>
    </Flex>
  );
}

export default PotCard;
